
import { useState } from 'react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { Poll } from '@/types/poll';
import { createQuestionsJson } from '@/utils/polls/pollDataTransformers';

export const usePollCardLink = (
  polls: Poll[], 
  setPolls: React.Dispatch<React.SetStateAction<Poll[]>>
) => {
  const [isLinking, setIsLinking] = useState(false);

  const linkPollToCard = async (pollId: string, cardId: string) => {
    try {
      setIsLinking(true);
      console.log(`Linking poll ${pollId} to card ${cardId}`);
      
      // Find the poll to update its linked_card_id in local state
      const poll = polls.find(p => p.id === pollId);
      if (!poll) {
        console.error('Poll not found in local state:', pollId);
        return;
      }

      // Create updated poll with the card ID
      const updatedPoll: Poll = { ...poll, linked_card_id: cardId };
      
      // Update the questions object in the database
      const updatedQuestions = createQuestionsJson(updatedPoll);

      const { error: pollUpdateError } = await supabase
        .from('polls')
        .update({ questions: updatedQuestions })
        .eq('id', pollId);
      
      if (pollUpdateError) {
        console.error('Failed to update poll data:', pollUpdateError);
        toast.error('Failed to update poll data');
        return;
      }
      
      // Create link in the poll_card_links table
      const { error: linkError } = await supabase
        .from('poll_card_links')
        .upsert({
          poll_id: pollId,
          card_id: cardId
        }, {
          onConflict: 'poll_id,card_id'
        });
        
      if (linkError) {
        console.error('Failed to link poll to card:', linkError);
        toast.error('Failed to link poll to card');
        return;
      }
      
      console.log('Successfully linked poll to card, updating statistics');
      
      // Call the database function to update the card statistics immediately
      const { error: updateStatsError } = await supabase.rpc('update_insight_card_stats', {
        input_poll_id: pollId
      });
      
      if (updateStatsError) {
        console.error('Error updating card statistics:', updateStatsError);
      }
      
      // Update local state
      setPolls(polls.map(poll => 
        poll.id === pollId ? { ...poll, linked_card_id: cardId } : poll
      ));
      
      toast.success('Poll linked to insight card');
    } catch (error) {
      console.error('Error linking poll to card:', error);
      toast.error('An error occurred while linking the poll');
    } finally {
      setIsLinking(false);
    }
  };

  return {
    linkPollToCard,
    isLinking
  };
};
